import type { Rng } from './rng';
import { idFor } from './ids';
import { YTD_FRACTION } from './finance';
import type {
  Application,
  CostActual,
  Product,
  Scenario,
  ScenarioAssumption,
  ScenarioMetric,
  ScenarioSectionData,
  Team,
} from './types';
import { round, timestampBefore } from './support';

/**
 * Three scenarios: today, the target product model, and the AI-enabled target.
 *
 * Every cell carries its formula, its formula version and its named inputs.
 * That is not documentation — it is the mechanism by which a chief financial
 * officer can click a saving of $118M and see that it is 1,900 applications
 * times a 22% consolidation rate times an 18% run-cost reduction, and then
 * argue with the 22% rather than with the tool.
 */

const FORMULA_VERSION = 'SCN-2026.1';

const BASE_ASSUMPTIONS: readonly ScenarioAssumption[] = [
  {
    assumptionCode: 'A01',
    label: 'Contractor rate premium over internal FTE',
    value: 0.46,
    unit: 'ratio',
    min: 0,
    max: 1.5,
    source: 'Procurement Cloud — FY2026 rate card',
    confidence: 'High',
    rationale: 'Blended contractor day rate against internal loaded cost.',
  },
  {
    assumptionCode: 'A02',
    label: 'Application consolidation rate',
    value: 0.22,
    unit: 'ratio',
    min: 0,
    max: 0.6,
    source: 'Duplicate-capability analysis',
    confidence: 'Medium',
    rationale:
      '16% of the estate is flagged as overlapping; 22% assumes some non-flagged retirement as well.',
  },
  {
    assumptionCode: 'A03',
    label: 'Target span of control',
    value: 8,
    unit: 'count',
    min: 4,
    max: 14,
    source: 'Target operating model',
    confidence: 'Medium',
    rationale: 'Product-aligned teams with fewer coordination roles.',
  },
  {
    assumptionCode: 'A04',
    label: 'Target management layers',
    value: 5,
    unit: 'count',
    min: 3,
    max: 9,
    source: 'Target operating model',
    confidence: 'Medium',
    rationale: 'CIO to engineer in five steps.',
  },
  {
    assumptionCode: 'A05',
    label: 'AI automation uptake on eligible work',
    value: 0.34,
    unit: 'ratio',
    min: 0,
    max: 0.7,
    source: 'AI use-case portfolio',
    confidence: 'Low',
    rationale:
      'Weighted by use cases already in production; the least evidenced assumption in the model.',
  },
  {
    assumptionCode: 'A06',
    label: 'Voluntary attrition',
    value: 0.11,
    unit: 'ratio',
    min: 0.04,
    max: 0.2,
    source: 'HCM Suite — trailing twelve months',
    confidence: 'High',
    rationale: 'Used to phase role reduction without compulsory exits.',
  },
  {
    assumptionCode: 'A07',
    label: 'Run-cost reduction per consolidated application',
    value: 0.18,
    unit: 'ratio',
    min: 0.05,
    max: 0.4,
    source: 'Prior consolidation programmes',
    confidence: 'Medium',
    rationale: 'Net of the retained integration surface.',
  },
  {
    assumptionCode: 'A08',
    label: 'Transition cost as a share of annual run',
    value: 0.12,
    unit: 'ratio',
    min: 0.04,
    max: 0.3,
    source: 'Transformation Office',
    confidence: 'Medium',
    rationale: 'One-off cost to reach the target state.',
  },
  {
    assumptionCode: 'A09',
    label: 'Benefit realisation lag',
    value: 9,
    unit: 'months',
    min: 0,
    max: 24,
    source: 'Transformation Office',
    confidence: 'Medium',
    rationale: 'Median lag from go-live to steady-state benefit.',
  },
  {
    assumptionCode: 'A10',
    label: 'Discount rate',
    value: 0.08,
    unit: 'ratio',
    min: 0.02,
    max: 0.15,
    source: 'Treasury',
    confidence: 'High',
    rationale: 'Group weighted average cost of capital.',
  },
];

function metric(
  metricId: string,
  label: string,
  unit: string,
  value: number | null,
  formula: string,
  inputs: { code: string; label: string; value: string }[],
  confidence: ScenarioMetric['confidence'],
  textValue: string | null = null,
): ScenarioMetric {
  return {
    metricId,
    label,
    unit,
    value,
    textValue,
    formula,
    formulaVersion: FORMULA_VERSION,
    inputs,
    confidence,
  };
}

export interface ScenarioInputs {
  asOf: string;
  rng: Rng;
  products: readonly Product[];
  applications: readonly Application[];
  teams: readonly Team[];
  costActuals: readonly CostActual[];
}

export function buildScenarios(inputs: ScenarioInputs): Scenario[] {
  const { asOf, products, applications, teams, costActuals } = inputs;

  const activeProducts = products.filter((p) => p.lifecycle === 'Active');
  const liveApplications = applications.filter(
    (a) => a.lifecycle === 'Active' || a.lifecycle === 'Sunset',
  );
  const applicationCount = liveApplications.length;
  const annualRunCents = liveApplications.reduce((sum, a) => sum + a.annualRunCostCents, 0);
  const internalFte = teams.reduce((sum, t) => sum + t.internalFte, 0);
  const contractorFte = teams.reduce((sum, t) => sum + t.contractorFte, 0);
  const totalFte = internalFte + contractorFte;
  const ytdCostCents = costActuals.reduce((sum, c) => sum + c.amountCents, 0);
  const annualisedCostCents = Math.round(ytdCostCents / YTD_FRACTION);

  const consolidationRate = 0.22;
  const runReduction = 0.18;
  const aiUptake = 0.34;

  const currentSpan = round(totalFte / Math.max(1, Math.round(totalFte / 5.4)), 1);

  const money = (cents: number): number => round(cents / 100, 2);

  const sections = (
    apps: number,
    runCents: number,
    fte: number,
    contractorShare: number,
    deploymentsPerMonth: number,
    leadTimeDays: number,
    unallocatedShare: number,
    confidence: ScenarioMetric['confidence'],
  ): ScenarioSectionData[] => [
    {
      sectionId: 'portfolio',
      title: 'Portfolio shape',
      metrics: [
        metric(
          'SM-001',
          'Active products',
          'count',
          activeProducts.length,
          'count(product where lifecycle = Active)',
          [{ code: 'PRODUCTS', label: 'Active products', value: String(activeProducts.length) }],
          'High',
        ),
        metric(
          'SM-002',
          'Applications in the estate',
          'count',
          apps,
          'count(application where lifecycle in (Active, Sunset)) × (1 − consolidationRate)',
          [
            { code: 'APPS', label: 'Applications today', value: String(applicationCount) },
            {
              code: 'A02',
              label: 'Consolidation rate',
              value: `${Math.round(consolidationRate * 100)}%`,
            },
          ],
          confidence,
        ),
        metric(
          'SM-003',
          'Applications per product',
          'ratio',
          round(apps / Math.max(1, activeProducts.length), 2),
          'applications ÷ active products',
          [
            { code: 'APPS', label: 'Applications', value: String(apps) },
            { code: 'PRODUCTS', label: 'Active products', value: String(activeProducts.length) },
          ],
          confidence,
        ),
      ],
    },
    {
      sectionId: 'cost',
      title: 'Cost',
      metrics: [
        metric(
          'SM-010',
          'Annual application run cost',
          'currency',
          money(runCents),
          'Σ application.annualRunCost × (1 − runCostReduction × consolidationRate)',
          [
            {
              code: 'RUN',
              label: 'Run cost today',
              value: `USD ${money(annualRunCents).toLocaleString('en-US')}`,
            },
            {
              code: 'A07',
              label: 'Run-cost reduction',
              value: `${Math.round(runReduction * 100)}%`,
            },
          ],
          confidence,
        ),
        metric(
          'SM-011',
          'Annualised total IT cost',
          'currency',
          money(Math.round(annualisedCostCents * (runCents / annualRunCents))),
          'annualised cost actuals × (target run ÷ current run)',
          [
            {
              code: 'YTD',
              label: 'Cost actuals year to date',
              value: `USD ${money(ytdCostCents).toLocaleString('en-US')}`,
            },
            {
              code: 'YTDF',
              label: 'Year-to-date fraction',
              value: round(YTD_FRACTION, 4).toString(),
            },
          ],
          confidence,
        ),
        metric(
          'SM-012',
          'Cost not attributable to a product',
          'percent',
          round(unallocatedShare, 4),
          'unallocated cost ÷ total cost',
          [
            {
              code: 'UNALLOC',
              label: 'Unallocated share',
              value: `${Math.round(unallocatedShare * 100)}%`,
            },
          ],
          confidence,
        ),
      ],
    },
    {
      sectionId: 'workforce',
      title: 'Workforce',
      metrics: [
        metric(
          'SM-020',
          'Total FTE',
          'fte',
          fte,
          'Σ team.internalFte + team.contractorFte, adjusted for the target operating model',
          [
            { code: 'FTE', label: 'FTE today', value: String(totalFte) },
            { code: 'A06', label: 'Attrition', value: '11%' },
          ],
          confidence,
        ),
        metric(
          'SM-021',
          'Contractor share',
          'percent',
          round(contractorShare, 4),
          'contractor FTE ÷ total FTE',
          [{ code: 'CONTRACTOR', label: 'Contractor FTE today', value: String(contractorFte) }],
          confidence,
        ),
      ],
    },
    {
      sectionId: 'delivery',
      title: 'Delivery',
      metrics: [
        metric(
          'SM-030',
          'Deployments per product per month',
          'deploymentsPerPeriod',
          round(deploymentsPerMonth, 2),
          'Σ deployments ÷ active products ÷ observation months',
          [{ code: 'DEPLOY', label: 'Deployments observed', value: 'trailing 6 months' }],
          confidence,
        ),
        metric(
          'SM-031',
          'Lead time for change',
          'days',
          round(leadTimeDays, 2),
          'mean(deployment.leadTimeHours) ÷ 24',
          [{ code: 'LEAD', label: 'Mean lead time', value: `${round(leadTimeDays, 1)} days` }],
          confidence,
        ),
      ],
    },
  ];

  const currentUnallocated = 0.23;

  const current: Scenario = {
    id: idFor('Scenario', 'SCN-CURRENT'),
    businessCode: 'SCN-CURRENT',
    type: 'Current',
    title: 'Current state',
    subtitle: 'The estate as the sources describe it today',
    colorToken: 'scenario.current',
    version: 3,
    status: 'Approved',
    isDirty: false,
    isReadOnly: true,
    subjectKind: 'Enterprise',
    subjectCode: 'ENT-AURORA',
    orgLayers: 8,
    averageSpan: currentSpan,
    managementRatio: round(1 / currentSpan, 3),
    sections: sections(
      applicationCount,
      annualRunCents,
      totalFte,
      contractorFte / Math.max(1, totalFte),
      3.4,
      4.9,
      currentUnallocated,
      'High',
    ),
    implementationMoves: [],
    assumptions: BASE_ASSUMPTIONS.slice(),
    lastRecalculatedAt: timestampBefore(asOf, 1, 340),
  };

  const targetApps = Math.round(applicationCount * (1 - consolidationRate));
  const targetRun = Math.round(annualRunCents * (1 - runReduction * consolidationRate));
  const targetFte = Math.round(totalFte * 0.94);
  const target: Scenario = {
    id: idFor('Scenario', 'SCN-TARGET'),
    businessCode: 'SCN-TARGET',
    type: 'TargetProductModel',
    title: 'Target product model',
    subtitle: 'Product-aligned teams, consolidated estate, allocated cost',
    colorToken: 'scenario.target',
    version: 5,
    status: 'InReview',
    isDirty: true,
    isReadOnly: false,
    subjectKind: 'Enterprise',
    subjectCode: 'ENT-AURORA',
    orgLayers: 5,
    averageSpan: 8,
    managementRatio: 0.125,
    sections: sections(targetApps, targetRun, targetFte, 0.19, 6.8, 2.6, 0.06, 'Medium'),
    implementationMoves: [
      'Stand up persistent product teams for the 24 Tier 1 and Tier 2 products.',
      'Consolidate the 312 duplicate-capability applications into 68 target platforms.',
      'Move third-party spend onto product-bearing statements of work so it can be attributed.',
      'Reduce management layers from eight to five and lift average span to eight.',
      'Retire the crew rostering monolith in favour of the recovery platform.',
    ],
    assumptions: BASE_ASSUMPTIONS.slice(),
    lastRecalculatedAt: timestampBefore(asOf, 4, 120),
  };

  const aiApps = Math.round(applicationCount * (1 - consolidationRate - 0.04));
  const aiRun = Math.round(targetRun * (1 - 0.07));
  const aiFte = Math.round(totalFte * (1 - aiUptake * 0.28));
  const aiEnabled: Scenario = {
    id: idFor('Scenario', 'SCN-AI'),
    businessCode: 'SCN-AI',
    type: 'AiEnabledTarget',
    title: 'AI-enabled target',
    subtitle: 'Target model with applied AI on eligible operational work',
    colorToken: 'scenario.ai',
    version: 2,
    status: 'Draft',
    isDirty: true,
    isReadOnly: false,
    subjectKind: 'Enterprise',
    subjectCode: 'ENT-AURORA',
    orgLayers: 5,
    averageSpan: 9.2,
    managementRatio: 0.109,
    sections: sections(aiApps, aiRun, aiFte, 0.16, 9.4, 1.8, 0.05, 'Low'),
    implementationMoves: [
      'Scale the 41 AI use cases already in production across the remaining value streams.',
      'Automate first-line service desk triage and routine roster queries.',
      'Apply recovery-option generation to irregular operations beyond crew.',
      'Reinvest a third of the released capacity into product engineering rather than removing it.',
    ],
    assumptions: BASE_ASSUMPTIONS.map((assumption) =>
      assumption.assumptionCode === 'A05'
        ? { ...assumption, value: aiUptake, confidence: 'Low' as const }
        : assumption,
    ),
    lastRecalculatedAt: null,
  };

  return [current, target, aiEnabled];
}

export const SCENARIO_FORMULA_VERSION = FORMULA_VERSION;
export const SCENARIO_ASSUMPTIONS = BASE_ASSUMPTIONS;
